import { first } from 'lodash';
import React, { useMemo } from 'react';
import { usePersistentDeliveryDates } from 'src/dish-first/usePersistentDeliveryDates';
import { DishFirstFoodItemPreviewFragment } from 'src/gqlReactTypings.generated.d';
import { useDishModalActions } from 'src/pages/consumer/dish-first/explore/store';
import { WavyEdge } from 'src/shared/design-system/WavyEdge';
import { MEAL_TYPE_CONTAINER_ID, MEAL_TYPE_SELECTOR } from '../../../Constants';
import { useGetCurrentMealType } from '../../../hooks';
import { SegmentHeaderTitle } from '../../../styles';
import { getMealTypeId, getMealTypeTitle } from '../../../utils';
import { ExploreDishCard } from '../ExploreDishCard';
import { DishFirstCardCollectionProps } from '../types';
import {
  Collection,
  CollectionAnchor,
  CollectionContainer,
  CollectionTitle,
  DividerContainer,
  Header,
  MaxWidthContainer,
} from './styles';
import { isFoodItem } from './utils';

export const DishFirstCardCollection: React.FC<DishFirstCardCollectionProps> = ({
  className,
  lineItemAvailabilities,
  segment,
  segmentMetrics,
  showHeader = true,
  scrollPosition,
}): JSX.Element => {
  const [deliveryDates] = usePersistentDeliveryDates();
  const deliveryDate = first(deliveryDates);
  const { openDishModal } = useDishModalActions();
  const items: DishFirstFoodItemPreviewFragment[] = useMemo(
    () => segment.content.items.filter(isFoodItem),
    [segment.content.items]
  );
  const { hasMultipleMealTypes, sortedItems } = useGetCurrentMealType({ foodItems: items });

  return (
    <MaxWidthContainer className={className}>
      {showHeader && (
        <Header>
          <SegmentHeaderTitle>{segment.displayName}</SegmentHeaderTitle>
        </Header>
      )}
      {hasMultipleMealTypes && <div id={MEAL_TYPE_CONTAINER_ID} />}
      {Object.entries(sortedItems).map(([key, currentFoodItems], index) =>
        currentFoodItems.length ? (
          <CollectionContainer key={key}>
            <CollectionAnchor data-role={MEAL_TYPE_SELECTOR} data-meal-type={key} id={getMealTypeId(key)} />
            {index !== 0 && hasMultipleMealTypes && (
              <DividerContainer>
                <WavyEdge position={WavyEdge.Position.TOP} isStroke waveSize={WavyEdge.Size.SHORT} />
              </DividerContainer>
            )}
            {hasMultipleMealTypes && <CollectionTitle>{getMealTypeTitle(key)}</CollectionTitle>}
            <Collection>
              {currentFoodItems?.map((item, idx) => (
                <ExploreDishCard
                  key={item.foodItemId}
                  idx={idx}
                  deliveryDate={deliveryDate}
                  foodItemPreview={item}
                  lineItemAvailabilities={lineItemAvailabilities}
                  onClick={openDishModal}
                  segment={segment}
                  segmentMetrics={segmentMetrics}
                  location='dish-first-explore-page'
                  isFluid
                  isResponsive={false}
                  scrollPosition={scrollPosition}
                  showDescription
                />
              ))}
            </Collection>
          </CollectionContainer>
        ) : null
      )}
    </MaxWidthContainer>
  );
};
